import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AsignacionItem, MatriculaResponse } from 'types/matricula.types';
import {
  AsistenciaResponse,
  AsistenciaItemPayload,
  EstadoAsistencia,
} from 'types/asistencia.types';
import { asignacionService } from 'services/matriculaService';
import { matriculaListService } from 'services/notaActividadService';
import { asistenciaService } from 'services/asistenciaService';

// ============================================================
// useAsistenciaDocente – flujo del docente
//   1. Seleccionar curso
//   2. Elegir fecha
//   3. Cargar estudiantes + asistencias existentes
//   4. Registrar (masivo) o actualizar (individual)
// ============================================================

// Estado local de asistencia por matriculaId antes de guardar
export type AsistenciaLocal = {
  estado: EstadoAsistencia;
  motivo: string;
};

export const useAsistenciaDocente = () => {
  const { user } = useAuth();

  // Paso 1 – cursos
  const [asignaciones, setAsignaciones]       = useState<AsignacionItem[]>([]);
  const [loadingAsig, setLoadingAsig]         = useState(true);
  const [selectedAsignacion, setSelected]     = useState<AsignacionItem | null>(null);

  // Paso 2 – fecha (por defecto hoy)
  const today = new Date().toISOString().split('T')[0];
  const [fecha, setFecha]                     = useState<string>(today);

  // Paso 3 – estudiantes y asistencias
  const [estudiantes, setEstudiantes]         = useState<MatriculaResponse[]>([]);
  const [asistencias, setAsistencias]         = useState<AsistenciaResponse[]>([]);
  const [loadingData, setLoadingData]         = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  // Estado local editable (matriculaId → { estado, motivo })
  const [localMap, setLocalMap]               = useState<Record<number, AsistenciaLocal>>({});

  // Submit
  const [submitting, setSubmitting]           = useState(false);
  const [submitError, setSubmitError]         = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess]     = useState(false);

  // Cargar asignaciones del docente al montar
  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoadingAsig(true);
      try {
        const data = await asignacionService.getByDocente(user.idUsuario);
        setAsignaciones(data.filter(a => a.activo));
      } catch {
        setAsignaciones([]);
      } finally { setLoadingAsig(false); }
    })();
  }, [user]);

  // Cargar estudiantes + asistencias cuando cambia asignacion o fecha
  const cargarDatos = useCallback(async () => {
    if (!selectedAsignacion) return;
    setLoadingData(true);
    setError(null);
    setSubmitSuccess(false);
    try {
      const [estudiantesData, asistenciasData] = await Promise.all([
        matriculaListService.listarPorGradoSeccionAnio(
          selectedAsignacion.gradoId,
          selectedAsignacion.seccionId,
          selectedAsignacion.anioEscolarId,
        ),
        asistenciaService.listarPorAsignacionYFecha(
          selectedAsignacion.idAsignacion,
          fecha,
        ),
      ]);
      setEstudiantes(estudiantesData);
      setAsistencias(asistenciasData);

      // Inicializar mapa local: si ya hay registro, usarlo; si no, PRESENTE por defecto
      const map: Record<number, AsistenciaLocal> = {};
      estudiantesData.forEach(est => {
        const existente = asistenciasData.find(a => a.matriculaId === est.idMatricula);
        map[est.idMatricula] = {
          estado: existente?.estado ?? 'PRESENTE',
          motivo: existente?.motivo ?? '',
        };
      });
      setLocalMap(map);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar datos');
    } finally { setLoadingData(false); }
  }, [selectedAsignacion, fecha]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Cambiar estado de un estudiante en el mapa local
  const setEstado = useCallback((matriculaId: number, estado: EstadoAsistencia) => {
    setLocalMap(prev => ({
      ...prev,
      [matriculaId]: { ...prev[matriculaId], estado, motivo: estado === 'PRESENTE' ? '' : prev[matriculaId]?.motivo ?? '' },
    }));
  }, []);

  const setMotivo = useCallback((matriculaId: number, motivo: string) => {
    setLocalMap(prev => ({
      ...prev,
      [matriculaId]: { ...prev[matriculaId], motivo },
    }));
  }, []);

  // Guardar: masivo si no hay registros, actualizar individual si ya existen
  const guardar = useCallback(async () => {
    if (!selectedAsignacion || !user || estudiantes.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const yaRegistrado = asistencias.length > 0;

      if (!yaRegistrado) {
        // Registro masivo (primera vez)
        const items: AsistenciaItemPayload[] = estudiantes.map(est => ({
          matriculaId: est.idMatricula,
          estado:      localMap[est.idMatricula]?.estado ?? 'PRESENTE',
          motivo:      localMap[est.idMatricula]?.motivo || null,
        }));
        const nuevas = await asistenciaService.registrarMasivo({
          asignacionAcademicaId: selectedAsignacion.idAsignacion,
          fecha,
          registradoPorId: user.idUsuario,
          asistencias: items,
        });
        setAsistencias(nuevas);
      } else {
        // Actualizar solo los que cambiaron
        const actualizaciones = asistencias
          .filter(a => {
            const local = localMap[a.matriculaId];
            return local && (local.estado !== a.estado || (local.motivo || null) !== a.motivo);
          })
          .map(a =>
            asistenciaService.actualizar(a.idAsistencia, {
              matriculaId:           a.matriculaId,
              asignacionAcademicaId: selectedAsignacion.idAsignacion,
              fecha,
              estado:  localMap[a.matriculaId].estado,
              motivo:  localMap[a.matriculaId].motivo || null,
              registradoPorId: user.idUsuario,
            })
          );
        const actualizadas = await Promise.all(actualizaciones);
        setAsistencias(prev =>
          prev.map(a => actualizadas.find(u => u.idAsistencia === a.idAsistencia) ?? a)
        );
      }

      setSubmitSuccess(true);
    } catch (e: any) {
      setSubmitError(e?.message ?? 'Error al guardar asistencias');
    } finally { setSubmitting(false); }
  }, [selectedAsignacion, user, fecha, estudiantes, asistencias, localMap]);

  const seleccionarAsignacion = useCallback((a: AsignacionItem) => {
    setSelected(a);
    setFecha(today);
    setEstudiantes([]);
    setAsistencias([]);
    setLocalMap({});
    setSubmitSuccess(false);
    setSubmitError(null);
  }, [today]);

  const volver = useCallback(() => {
    setSelected(null);
    setEstudiantes([]);
    setAsistencias([]);
    setLocalMap({});
    setError(null);
    setSubmitError(null);
    setSubmitSuccess(false);
  }, []);

  const yaRegistrado = asistencias.length > 0;

  return {
    // Asignaciones
    asignaciones, loadingAsig, selectedAsignacion, seleccionarAsignacion, volver,
    // Fecha
    fecha, setFecha,
    // Datos
    estudiantes, asistencias, loadingData, error, yaRegistrado,
    // Mapa local
    localMap, setEstado, setMotivo,
    // Submit
    submitting, submitError, submitSuccess, guardar,
  };
};
